"use client";

import { useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  FileText,
  FileUp,
  Gauge,
  Landmark,
  Link2,
  Megaphone,
  PackageOpen,
  Radar,
  ShieldCheck,
  Sparkles,
  Trophy,
  Waypoints,
  X,
  type LucideIcon,
} from "lucide-react";
import { useAppContext } from "@/contexts/app";
import { isAuthEnabled } from "@/lib/auth";
import { trackEvent } from "@/lib/analytics";
import {
  createMiroFishClientSubmissionId,
  createMiroFishProjectFromFiles,
  createMiroFishProjectFromUrl,
} from "@/lib/mirofish-api";
import {
  buildPredictionPrompt,
  type PredictionDomainIcon,
  type PredictionPageConfig,
  type PredictionWorkflowIcon,
} from "@/content/prediction-page";
import MiroFishSiteFooter from "./site-footer";
import MiroFishSiteHeader from "./site-header";

const MAX_SOURCE_DOCUMENT_BYTES = 50 * 1024 * 1024;

type SourceMode = "upload" | "link";

const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

const isAllowedSourceDocument = (file: File) => {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".pdf") ||
    name.endsWith(".md") ||
    name.endsWith(".markdown") ||
    name.endsWith(".txt")
  );
};

const isValidSourceUrl = (value: string) => {
  try {
    return new URL(value.trim()).protocol === "https:";
  } catch {
    return false;
  }
};

const formatFileSize = (size: number) => {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.ceil(size / 1024))} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const domainIcons: Record<PredictionDomainIcon, LucideIcon> = {
  sports: Trophy,
  audience: Megaphone,
  policy: Landmark,
  product: PackageOpen,
  market: BarChart3,
  story: BookOpen,
};

const workflowIcons: Record<PredictionWorkflowIcon, LucideIcon> = {
  evidence: FileText,
  world: Waypoints,
  forecast: Radar,
};

export default function PredictionExperiencePage({
  config,
}: {
  config: PredictionPageConfig;
}) {
  const router = useRouter();
  const { user, setShowSignModal, setUser } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const linkSubmissionRef = useRef<{ key: string; id: string } | null>(null);

  const [scope, setScope] = useState(config.lab.defaultScope);
  const [lens, setLens] = useState(config.lab.defaultLens);
  const [subjectA, setSubjectA] = useState("");
  const [subjectB, setSubjectB] = useState("");
  const [context, setContext] = useState("");
  const [sourceMode, setSourceMode] = useState<SourceMode>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [sourceUrl, setSourceUrl] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchProgress, setLaunchProgress] = useState(0);
  const [launchStepLabel, setLaunchStepLabel] = useState<string | null>(null);

  const authEnabled = isAuthEnabled();
  const isSignedIn = authEnabled ? Boolean(user?.uuid || user?.email) : true;
  const totalFileBytes = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files]
  );
  const subjectsMatch =
    subjectA.trim().length > 0 &&
    subjectA.trim().toLocaleLowerCase() ===
      subjectB.trim().toLocaleLowerCase();
  const prompt = useMemo(
    () =>
      buildPredictionPrompt(config, {
        scope,
        subjectA,
        subjectB,
        lens,
        context,
      }),
    [config, context, lens, scope, subjectA, subjectB]
  );
  const sourceReady =
    sourceMode === "upload"
      ? files.length > 0 && !fileError && totalFileBytes <= MAX_SOURCE_DOCUMENT_BYTES
      : isValidSourceUrl(sourceUrl) && !linkError;
  const canLaunch =
    subjectA.trim().length > 0 &&
    subjectB.trim().length > 0 &&
    !subjectsMatch &&
    sourceReady &&
    !isCheckingAccess &&
    !isLaunching;

  const displaySubjectA = subjectA.trim() || config.hero.subjectAFallback;
  const displaySubjectB = subjectB.trim() || config.hero.subjectBFallback;
  const displayScope =
    config.lab.scopes.find((item) => item.value === scope)?.label ??
    config.hero.scopeFallback;
  const DomainIcon = domainIcons[config.domainIcon];
  const eventName = (suffix: string) => `${config.analyticsPrefix}_${suffix}`;

  const setMode = (nextMode: SourceMode) => {
    setSourceMode(nextMode);
    setError(null);
    setFileError(null);
    setLinkError(null);
    setLaunchProgress(0);
    setLaunchStepLabel(null);
    trackEvent(eventName("source_mode_change"), {
      source_mode: nextMode,
    });
  };

  const appendFiles = (selectedFiles: File[]) => {
    const unsupported = selectedFiles.filter(
      (file) => !isAllowedSourceDocument(file)
    );
    if (unsupported.length) {
      setFileError("Use PDF, Markdown, or plain-text source documents.");
      return;
    }

    const nextFiles = [...files];
    const seen = new Set(nextFiles.map(fileKey));
    for (const file of selectedFiles) {
      if (!seen.has(fileKey(file))) {
        nextFiles.push(file);
        seen.add(fileKey(file));
      }
    }

    const nextTotal = nextFiles.reduce((sum, file) => sum + file.size, 0);
    if (nextTotal > MAX_SOURCE_DOCUMENT_BYTES) {
      setFileError("Upload at most 50 MB of source documents per simulation.");
      return;
    }

    setFiles(nextFiles);
    setFileError(null);
    setError(null);
  };

  const removeFile = (key: string) => {
    const nextFiles = files.filter((file) => fileKey(file) !== key);
    setFiles(nextFiles);
    setFileError(null);
  };

  const redirectToPricing = () => {
    router.push(
      `/pricing?intent=start-engine&return_to=${encodeURIComponent(
        `${config.path}#prediction-lab`
      )}`
    );
  };

  const resolveAccess = async () => {
    if (!authEnabled) {
      return true;
    }

    if (!isSignedIn) {
      setShowSignModal(true);
      trackEvent(eventName("access_gate"), { result: "sign_in" });
      return false;
    }

    if (user?.hasActiveSubscription === true) {
      return true;
    }

    if (user?.hasActiveSubscription === false) {
      redirectToPricing();
      trackEvent(eventName("access_gate"), { result: "pricing" });
      return false;
    }

    setIsCheckingAccess(true);
    try {
      const response = await fetch("/api/get-user-info", { method: "POST" });
      const payload = await response.json().catch(() => null);

      if (response.status === 401 || payload?.code === -2) {
        setShowSignModal(true);
        trackEvent(eventName("access_gate"), { result: "sign_in" });
        return false;
      }

      if (!response.ok || payload?.code !== 0 || !payload?.data) {
        setError(
          typeof payload?.message === "string" && payload.message.trim()
            ? payload.message
            : "Unable to verify your subscription. Please try again."
        );
        return false;
      }

      setUser(payload.data);
      if (payload.data.hasActiveSubscription === true) {
        return true;
      }

      redirectToPricing();
      trackEvent(eventName("access_gate"), { result: "pricing" });
      return false;
    } catch {
      setError("Unable to verify your subscription. Please try again.");
      return false;
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleLaunch = async () => {
    if (!canLaunch) {
      return;
    }

    setError(null);
    const accessAllowed = await resolveAccess();
    if (!accessAllowed) {
      return;
    }

    setIsLaunching(true);
    setLaunchProgress(8);
    setLaunchStepLabel(
      sourceMode === "link"
        ? config.lab.preparingLinkLabel
        : config.lab.preparingUploadLabel
    );
    trackEvent(eventName("launch"), {
      scope,
      lens,
      source_mode: sourceMode,
      source_document_count: sourceMode === "upload" ? files.length : 1,
    });

    try {
      const options = {
        onProgress: ({ progress, label }: { progress: number; label: string }) => {
          setLaunchProgress((current) => Math.max(current, progress));
          setLaunchStepLabel(label);
        },
        onRetry: ({ retry, maxRetries }: { retry: number; maxRetries: number }) => {
          setLaunchProgress((current) => Math.max(current, 18 + retry * 8));
          setLaunchStepLabel(`Reconnecting... retry ${retry}/${maxRetries}`);
        },
      };
      const response =
        sourceMode === "link"
          ? await createMiroFishProjectFromUrl({
              prompt,
              url: sourceUrl.trim(),
              clientSubmissionId: (() => {
                const key = `${prompt}\n${sourceUrl.trim()}`;
                if (linkSubmissionRef.current?.key !== key) {
                  linkSubmissionRef.current = {
                    key,
                    id: createMiroFishClientSubmissionId(),
                  };
                }
                return linkSubmissionRef.current.id;
              })(),
              options,
            })
          : await createMiroFishProjectFromFiles({
              prompt,
              files,
              options,
            });

      setLaunchProgress(100);
      setLaunchStepLabel("Prediction project ready, redirecting...");
      linkSubmissionRef.current = null;
      trackEvent(eventName("launch_success"), {
        scope,
        lens,
        source_mode: sourceMode,
      });
      router.push(`/process/${response.project.projectId}`);
    } catch (launchError) {
      const message =
        launchError instanceof Error
          ? launchError.message
          : "Failed to create the prediction project.";
      if (message.toLowerCase().includes("insufficient credits")) {
        redirectToPricing();
      } else {
        setError(message);
      }
      setLaunchProgress(0);
      setLaunchStepLabel(null);
      trackEvent(eventName("launch_failure"), {
        scope,
        lens,
        source_mode: sourceMode,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#04050a] text-white">
      <MiroFishSiteHeader hashHrefPrefix="/" />

      <main id="main-content">
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-[-8%] top-[-10%] h-[420px] w-[420px] rounded-full bg-emerald-500/12 blur-[140px]" />
            <div className="absolute right-[-6%] top-[6%] h-[440px] w-[440px] rounded-full bg-sky-500/14 blur-[150px]" />
            <div className="absolute bottom-[-25%] left-[34%] h-[360px] w-[360px] rounded-full bg-orange-500/12 blur-[140px]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-24 lg:pt-16">
            <nav
              aria-label="Breadcrumb"
              className="mb-8 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-white/42"
            >
              <a href="/" className="transition hover:text-white/80">
                MiroFish
              </a>
              <span aria-hidden="true">/</span>
              <span className="text-emerald-200/80">
                {config.breadcrumbLabel}
              </span>
            </nav>

            <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(520px,1.1fr)]">
              <div className="relative z-10 max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/[0.07] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-100/80">
                  <DomainIcon className="h-3.5 w-3.5" />
                  {config.hero.eyebrow}
                </div>
                <h1 className="mt-7 text-balance text-5xl font-semibold leading-[0.94] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
                  {config.hero.title}
                </h1>
                <p className="mt-6 max-w-xl text-base leading-8 text-white/64 sm:text-lg">
                  {config.hero.description}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#prediction-lab"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black transition hover:bg-emerald-50"
                  >
                    {config.hero.primaryCta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <a
                    href="#how-it-works"
                    className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/[0.09]"
                  >
                    {config.hero.secondaryCta}
                  </a>
                </div>
                <div className="mt-7 flex items-start gap-3 text-sm leading-6 text-white/48">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                  <p>{config.hero.boundary}</p>
                </div>
              </div>

              <div
                className="group relative aspect-[3/2] overflow-hidden rounded-[34px] border border-white/14 bg-[#07110f] shadow-[0_32px_120px_rgba(0,0,0,0.62)]"
                data-testid={`${config.testIdPrefix}-match-board`}
              >
                <Image
                  src={config.hero.imageSrc}
                  alt={config.hero.imageAlt}
                  fill
                  priority
                  quality={88}
                  sizes="(min-width: 1024px) 55vw, 100vw"
                  className="object-cover transition duration-700 ease-out group-hover:scale-[1.015] motion-reduce:transform-none"
                  style={{ objectPosition: config.hero.imageObjectPosition }}
                />
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,12,0.72)_0%,rgba(2,6,12,0.08)_38%,rgba(2,6,12,0.12)_55%,rgba(2,6,12,0.9)_100%)]" />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_64%_48%,transparent_22%,rgba(1,5,10,0.34)_100%)]" />
                <div className="pointer-events-none absolute inset-4 rounded-[24px] border border-white/14 sm:inset-5 sm:rounded-[26px]" />

                <div className="absolute inset-x-5 top-5 z-10 flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-[0.13em] text-white/72 sm:inset-x-7 sm:top-7 sm:gap-3 sm:text-[10px] sm:tracking-[0.2em]">
                  <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-white/12 bg-black/38 px-3 py-2 backdrop-blur-md">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_16px_rgba(110,231,183,0.9)]" />
                    {config.hero.viewLabel}
                  </span>
                  <span className="shrink-0 whitespace-nowrap rounded-full border border-white/12 bg-black/38 px-3 py-2 backdrop-blur-md">
                    {displayScope}
                  </span>
                </div>

                <svg
                  aria-hidden="true"
                  viewBox="0 0 100 66"
                  className="pointer-events-none absolute inset-0 h-full w-full"
                  fill="none"
                >
                  <path
                    d="M18 48 C 31 44, 36 29, 50 31 S 66 44, 81 20"
                    stroke="rgba(110, 231, 183, 0.72)"
                    strokeWidth="0.48"
                    strokeLinecap="round"
                    strokeDasharray="1.5 2.1"
                  />
                  <circle cx="18" cy="48" r="1.3" fill="rgba(110, 231, 183, 0.9)" />
                  <circle cx="50" cy="31" r="1.1" fill="rgba(255, 255, 255, 0.82)" />
                  <circle cx="81" cy="20" r="1.3" fill="rgba(251, 191, 36, 0.88)" />
                </svg>

                <div className="absolute inset-x-5 bottom-5 z-10 rounded-[22px] border border-white/12 bg-black/52 px-4 py-3.5 shadow-[0_18px_60px_rgba(0,0,0,0.34)] backdrop-blur-lg sm:inset-x-7 sm:bottom-7 sm:flex sm:items-end sm:justify-between sm:gap-6 sm:px-5 sm:py-4">
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-200/72">
                      {config.hero.scenarioLabel}
                    </p>
                    <p className="mt-1.5 truncate text-sm font-semibold text-white sm:text-base">
                      {displaySubjectA}
                      <span className="mx-2 font-mono text-[10px] font-normal uppercase text-white/42">
                        {config.hero.subjectConnector}
                      </span>
                      {displaySubjectB}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.16em] text-white/48 sm:mt-0 sm:shrink-0">
                    <span>{config.hero.signalLabel}</span>
                    <span aria-hidden="true" className="text-white/20">/</span>
                    <span>{config.hero.provenanceLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/8 bg-white/[0.025]">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-4 py-8 sm:px-6 lg:grid-cols-[repeat(4,1fr)_1.7fr] lg:px-8">
            {config.facts.items.map((fact) => (
              <div key={fact.label} className="px-3 py-3 lg:px-5">
                <div className="font-mono text-2xl font-semibold text-white">
                  {fact.value}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.16em] text-white/38">
                  {fact.label}
                </div>
              </div>
            ))}
            <div className="col-span-2 flex flex-col justify-center border-t border-white/8 px-3 pt-6 text-sm leading-6 text-white/50 lg:col-span-1 lg:border-l lg:border-t-0 lg:px-6 lg:pt-0">
              <p>{config.facts.note}</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {config.facts.sources.map((source) => (
                  <a
                    key={source.href}
                    href={source.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-200/75 underline decoration-white/20 underline-offset-4 hover:text-emerald-100"
                  >
                    {source.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          id="prediction-lab"
          className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
          data-testid={`${config.testIdPrefix}-prediction-lab`}
        >
          <div className="max-w-3xl">
            <div className="font-mono text-xs uppercase tracking-[0.24em] text-emerald-200/70">
              {config.lab.eyebrow}
            </div>
            <h2 className="mt-4 text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
              {config.lab.title}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-white/58">
              {config.lab.description}
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="rounded-[32px] border border-white/10 bg-white/[0.045] p-5 sm:p-7">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-300/[0.08] text-emerald-200">
                  <DomainIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">
                    {config.lab.setupEyebrow}
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">
                    {config.lab.setupTitle}
                  </h3>
                </div>
              </div>

              <label className="mt-7 block text-sm font-medium text-white/72">
                {config.lab.scopeLabel}
                <select
                  value={scope}
                  onChange={(event) => {
                    const nextScope = event.target.value;
                    setScope(nextScope);
                    trackEvent(eventName("scope_change"), {
                      scope: nextScope,
                    });
                  }}
                  className="mt-2 w-full rounded-2xl border border-white/12 bg-[#090d12] px-4 py-3 text-white outline-none transition focus:border-emerald-300/45"
                  data-testid={`${config.testIdPrefix}-stage`}
                >
                  {config.lab.scopes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-white/72">
                  {config.lab.subjectA.label}
                  <input
                    value={subjectA}
                    onChange={(event) => setSubjectA(event.target.value)}
                    maxLength={80}
                    placeholder={config.lab.subjectA.placeholder}
                    className="mt-2 w-full rounded-2xl border border-white/12 bg-[#090d12] px-4 py-3 text-white placeholder:text-white/25 focus:border-sky-300/50"
                    data-testid={`${config.testIdPrefix}-team-a`}
                  />
                </label>
                <label className="text-sm font-medium text-white/72">
                  {config.lab.subjectB.label}
                  <input
                    value={subjectB}
                    onChange={(event) => setSubjectB(event.target.value)}
                    maxLength={80}
                    placeholder={config.lab.subjectB.placeholder}
                    className="mt-2 w-full rounded-2xl border border-white/12 bg-[#090d12] px-4 py-3 text-white placeholder:text-white/25 focus:border-orange-300/50"
                    data-testid={`${config.testIdPrefix}-team-b`}
                  />
                </label>
              </div>
              {subjectsMatch ? (
                <p className="mt-3 text-sm text-rose-300" role="alert">
                  {config.lab.duplicateSubjectsError}
                </p>
              ) : null}

              <fieldset className="mt-7">
                <legend className="text-sm font-medium text-white/72">
                  {config.lab.lensLabel}
                </legend>
                <div className="mt-3 grid gap-3">
                  {config.lab.lenses.map((item) => {
                    const active = item.value === lens;
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => {
                          setLens(item.value);
                          trackEvent(eventName("lens_change"), {
                            lens: item.value,
                          });
                        }}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          active
                            ? "border-emerald-300/35 bg-emerald-300/[0.09]"
                            : "border-white/10 bg-black/20 hover:bg-white/[0.05]"
                        }`}
                        aria-pressed={active}
                      >
                        <span className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                              active
                                ? "border-emerald-300 bg-emerald-300 text-black"
                                : "border-white/20"
                            }`}
                          >
                            {active ? <Check className="h-3 w-3" /> : null}
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-white">
                              {item.label}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-white/45">
                              {item.description}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <label className="mt-7 block text-sm font-medium text-white/72">
                {config.lab.contextLabel}{" "}
                <span className="text-white/35">(optional)</span>
                <textarea
                  value={context}
                  onChange={(event) => setContext(event.target.value)}
                  maxLength={800}
                  rows={4}
                  placeholder={config.lab.contextPlaceholder}
                  className="mt-2 w-full resize-none rounded-2xl border border-white/12 bg-[#090d12] px-4 py-3 leading-6 text-white placeholder:text-white/25 focus:border-emerald-300/45"
                  data-testid={`${config.testIdPrefix}-context`}
                />
              </label>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.065),rgba(255,255,255,0.025))] p-5 sm:p-7">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-sky-300/20 bg-sky-300/[0.08] text-sky-200">
                    <FileUp className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">
                      {config.lab.evidenceEyebrow}
                    </div>
                    <h3 className="mt-1 text-xl font-semibold">
                      {config.lab.evidenceTitle}
                    </h3>
                  </div>
                </div>
                <span className="hidden rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/38 sm:inline-flex">
                  50 MB max
                </span>
              </div>

              <div className="mt-7 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-black/25 p-1.5">
                <button
                  type="button"
                  onClick={() => setMode("upload")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                    sourceMode === "upload"
                      ? "bg-white text-black"
                      : "text-white/52 hover:text-white"
                  }`}
                  data-testid={`${config.testIdPrefix}-upload-tab`}
                >
                  <FileUp className="h-4 w-4" />
                  Upload files
                </button>
                <button
                  type="button"
                  onClick={() => setMode("link")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                    sourceMode === "link"
                      ? "bg-white text-black"
                      : "text-white/52 hover:text-white"
                  }`}
                  data-testid={`${config.testIdPrefix}-link-tab`}
                >
                  <Link2 className="h-4 w-4" />
                  Public link
                </button>
              </div>

              {sourceMode === "upload" ? (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      appendFiles(Array.from(event.dataTransfer.files));
                    }}
                    className="flex min-h-40 w-full flex-col items-center justify-center rounded-[24px] border border-dashed border-white/15 bg-black/25 px-5 py-8 text-center transition hover:border-emerald-300/35 hover:bg-emerald-300/[0.035]"
                  >
                    <FileUp className="h-6 w-6 text-emerald-200" />
                    <span className="mt-3 text-sm font-semibold">
                      {config.lab.dropTitle}
                    </span>
                    <span className="mt-1 text-xs leading-5 text-white/38">
                      PDF, Markdown, or TXT · click to browse
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain"
                    multiple
                    className="sr-only"
                    onChange={(event) => {
                      appendFiles(Array.from(event.target.files || []));
                      event.target.value = "";
                    }}
                    data-testid={`${config.testIdPrefix}-file-input`}
                  />

                  {files.length ? (
                    <div className="mt-4 space-y-2">
                      {files.map((file) => (
                        <div
                          key={fileKey(file)}
                          className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-white/78">
                              {file.name}
                            </p>
                            <p className="mt-0.5 font-mono text-[10px] text-white/32">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(fileKey(file))}
                            aria-label={`Remove ${file.name}`}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/45 transition hover:bg-white/10 hover:text-white"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <label className="mt-5 block text-sm font-medium text-white/72">
                  {config.lab.publicLinkLabel}
                  <input
                    value={sourceUrl}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSourceUrl(value);
                      setLinkError(
                        value.trim() && !isValidSourceUrl(value)
                          ? "Enter a valid public HTTPS link."
                          : null
                      );
                    }}
                    placeholder={config.lab.publicLinkPlaceholder}
                    className="mt-2 w-full rounded-2xl border border-white/12 bg-[#090d12] px-4 py-3 text-white placeholder:text-white/25 focus:border-sky-300/45"
                    data-testid={`${config.testIdPrefix}-source-url`}
                  />
                  <span className="mt-2 block text-xs leading-5 text-white/38">
                    {config.lab.publicLinkHelp}
                  </span>
                </label>
              )}

              {fileError || linkError ? (
                <p className="mt-4 text-sm text-rose-300" role="alert">
                  {fileError || linkError}
                </p>
              ) : null}

              <div className="mt-6 rounded-[24px] border border-white/10 bg-black/30 p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/38">
                  <Sparkles className="h-3.5 w-3.5 text-sky-200" />
                  Simulation brief preview
                </div>
                <p
                  className="mt-4 text-sm leading-7 text-white/65"
                  data-testid={`${config.testIdPrefix}-prompt-preview`}
                >
                  {prompt}
                </p>
              </div>

              {launchStepLabel ? (
                <div className="mt-5" aria-live="polite">
                  <div className="flex items-center justify-between gap-4 text-xs text-white/48">
                    <span>{launchStepLabel}</span>
                    <span className="font-mono">{Math.round(launchProgress)}%</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/8">
                    <div
                      className="h-full rounded-full bg-[linear-gradient(90deg,#34d399,#7dd3fc)] transition-[width] duration-300"
                      style={{ width: `${Math.max(4, launchProgress)}%` }}
                    />
                  </div>
                </div>
              ) : null}

              {error ? (
                <p className="mt-4 text-sm leading-6 text-rose-300" role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="button"
                onClick={handleLaunch}
                disabled={!canLaunch}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/28"
                data-testid={`${config.testIdPrefix}-launch`}
              >
                {isCheckingAccess
                  ? "Checking access..."
                  : isLaunching
                    ? "Building prediction..."
                    : authEnabled && !isSignedIn
                      ? "Sign in to run the simulation"
                      : config.lab.launchLabel}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-center text-xs leading-5 text-white/32">
                {config.lab.outputDisclaimer}
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-white/8 bg-white/[0.025]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.24em] text-sky-200/65">
                  {config.report.eyebrow}
                </div>
                <h2 className="mt-4 text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
                  {config.report.title}
                </h2>
              </div>
              <p className="max-w-2xl text-base leading-8 text-white/55 lg:justify-self-end">
                {config.report.description}
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {config.report.outputs.map((item, index) => (
                <article
                  key={item.title}
                  className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#080a10] p-6"
                >
                  <div className="font-mono text-xs text-emerald-200/55">
                    OUTPUT 0{index + 1}
                  </div>
                  <h3 className="mt-8 text-2xl font-semibold">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/52">
                    {item.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
        >
          <div className="max-w-3xl">
            <div className="font-mono text-xs uppercase tracking-[0.24em] text-orange-200/65">
              {config.workflow.eyebrow}
            </div>
            <h2 className="mt-4 text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
              {config.workflow.title}
            </h2>
          </div>
          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {config.workflow.steps.map((step, index) => {
              const Icon = workflowIcons[step.icon];
              return (
                <article
                  key={step.title}
                  className="rounded-[28px] border border-white/10 bg-white/[0.04] p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/30 text-emerald-200">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-mono text-xs text-white/28">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-7 text-2xl font-semibold">{step.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/52">
                    {step.description}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col gap-4 rounded-[28px] border border-amber-300/15 bg-amber-300/[0.045] p-6 sm:flex-row sm:items-start">
            <Gauge className="h-6 w-6 shrink-0 text-amber-200" />
            <div>
              <h3 className="text-lg font-semibold">
                {config.workflow.boundaryTitle}
              </h3>
              <p className="mt-2 max-w-4xl text-sm leading-7 text-white/52">
                {config.workflow.boundaryDescription}
              </p>
            </div>
          </div>
        </section>

        <section className="border-t border-white/8 bg-white/[0.02]">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="text-center">
              <div className="font-mono text-xs uppercase tracking-[0.24em] text-white/36">
                {config.faq.eyebrow}
              </div>
              <h2 className="mt-4 text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
                {config.faq.title}
              </h2>
            </div>
            <div className="mt-10 divide-y divide-white/10 overflow-hidden rounded-[30px] border border-white/10 bg-white/[0.04]">
              {config.faq.items.map((item) => (
                <details key={item.question} className="group px-5 py-5 sm:px-7">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left text-base font-semibold sm:text-lg">
                    {item.question}
                    <span className="font-mono text-white/32 transition group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 max-w-3xl text-sm leading-7 text-white/52">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
            <p className="mt-7 text-center text-xs leading-6 text-white/32">
              {config.faq.footnote}
            </p>
          </div>
        </section>
      </main>

      <MiroFishSiteFooter />
    </div>
  );
}
